import { isEmpty } from '@fruitful/fruitils';
import {
    calculateNewRulePriority,
    findGroupById,
    findRuleByIdentifier,
    getAccountWithDetails,
    getCategorizedRulesAndTotals,
} from '@fruitful/money-map';
import {
    type DeepPartial,
    MoneyMapCategory,
    MoneyMapRuleType,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { keyBy } from 'lodash';
import { useMemo, useState } from 'react';
import { XStack, YStack } from 'smoothie';

import { selectedMembershipAtom } from '../../../entities/entityAtoms';
import { useMoneyMapAccounts } from '../../providers/MoneyMapAccountsProvider';
import {
    GoalPreset,
    GoalPresetHelpText,
    GoalPresetRuleConfigs,
} from '../../types/GoalPresets';
import { getMoneyMapperEffectiveMonthlyIncomeCents } from '../../utils/moneyMapperEffectiveIncome';
import BillsSpendForm from '../forms/BillsSpendForm';
import GoalForm from '../forms/GoalForm';
import GoalPresetSelection from '../forms/GoalPresetSelection';
import { GroupForm } from '../forms/GroupForm';
import IncomeForm from '../forms/IncomeForm';
import OtherAccountsForm from '../forms/OtherAccountsForm';
import EditorSectionContainer from './EditorSectionContainer';

enum FormState {
    CLOSED = 'CLOSED',
    INCOME = 'INCOME',
    BILLS = 'BILLS',
    SPEND = 'SPEND',
    GOALS = 'GOALS',
    OTHER_ACCOUNTS = 'OTHER_ACCOUNTS',
    GOAL_PRESETS = 'GOAL_PRESETS',
    GROUP = 'GROUP',
}

interface MoneyMapEditorProps {
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
    onRemoveRule: (ruleId: string) => Promise<void>;
    onRemoveGroup: (groupId: string) => Promise<void>;
    formPresentation?: 'drawer' | 'modal';
}

const MoneyMapEditor = ({
    moneyMap,
    onUpdateMoneyMap,
    onRemoveRule,
    onRemoveGroup,
    formPresentation = 'drawer',
}: MoneyMapEditorProps) => {
    const { accounts } = useMoneyMapAccounts();
    const accountsKeyedById = keyBy(accounts, 'id');

    const membership = useAtomValue(selectedMembershipAtom);

    const [formState, setFormState] = useState(FormState.CLOSED);
    const [selectedRuleIdentifier, setSelectedRuleIdentifier] = useState<
        string | null
    >(null);
    const [rulePreset, setRulePreset] = useState<GoalPreset | null>(null);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

    const clearSelections = () => {
        setSelectedRuleIdentifier(null);
        setRulePreset(null);
        setSelectedGroupId(null);
    };

    const closeForm = () => {
        clearSelections();
        setFormState(FormState.CLOSED);
    };

    const onSaveForm = async (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ): Promise<void> => {
        closeForm();

        await onUpdateMoneyMap(updates);
    };

    const onPressRemoveRule = async (ruleId?: string) => {
        closeForm();

        if (ruleId != null) {
            await onRemoveRule(ruleId);
        }
    };

    const onPressRemoveGroup = async (groupId?: string) => {
        closeForm();

        if (groupId != null) {
            await onRemoveGroup(groupId);
        }
    };

    const {
        billRules,
        spendRules,
        goalRules,
        otherAccountRules,
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
    } = useMemo(() => {
        return getCategorizedRulesAndTotals(moneyMap, accounts);
    }, [JSON.stringify(accounts), JSON.stringify(moneyMap)]);

    const getCtaButtonText = (category: MoneyMapCategory): string => {
        switch (category) {
            case MoneyMapCategory.INCOME:
                return moneyMap.incomeMoneyMapAccountId != null
                    ? 'Edit Income'
                    : 'Add Income';
            case MoneyMapCategory.BILLS:
                return 'Add Bills account';
            case MoneyMapCategory.SPEND:
                return 'Add Spend account';
            case MoneyMapCategory.GOALS:
                return 'Add Goal';
            case MoneyMapCategory.OTHER_ACCOUNTS:
                return 'Add account';
        }
    };

    const selectedBillsRule = findRuleByIdentifier(
        billRules,
        selectedRuleIdentifier,
    );
    const selectedSpendRule = findRuleByIdentifier(
        spendRules,
        selectedRuleIdentifier,
    );
    const selectedGoalRule = findRuleByIdentifier(
        goalRules,
        selectedRuleIdentifier,
    );
    const selectedOtherAccountRule = findRuleByIdentifier(
        otherAccountRules,
        selectedRuleIdentifier,
    );
    const selectedGroup = findGroupById(
        moneyMap.moneyMapRuleGroups ?? [],
        selectedGroupId,
    );

    const effectiveMonthlyIncomeCents = useMemo(
        () =>
            getMoneyMapperEffectiveMonthlyIncomeCents({
                moneyMap,
                membershipIncomes: membership?.membershipIncomes,
            }),
        [JSON.stringify(moneyMap), membership?.membershipIncomes],
    );

    // Require income (stored take-home and/or membership income sources) plus routing account
    const disableNonIncomeSections =
        effectiveMonthlyIncomeCents <= 0 ||
        (moneyMap.incomeMoneyMapAccountId == null &&
            isEmpty(moneyMap.incomeMoneyMapAccount));

    return (
        <XStack width="100%">
            <YStack
                marginTop={24}
                paddingBottom={24}
                flex={1}
                onPress={() => {
                    if (formState !== FormState.CLOSED) {
                        clearSelections();
                        setFormState(FormState.CLOSED);
                    }
                }}
            >
                <YStack gap={24} alignSelf="center">
                    <EditorSectionContainer
                        category={MoneyMapCategory.INCOME}
                        amountCents={moneyMap.monthlyIncomeCents ?? null}
                        incomeAccount={getAccountWithDetails(
                            accountsKeyedById,
                            moneyMap.incomeMoneyMapAccountId,
                            moneyMap.incomeMoneyMapAccount,
                        )}
                        moneyMap={moneyMap}
                        onPressIncomeAccount={() => {
                            setFormState(FormState.INCOME);
                        }}
                        ctaButtonText={getCtaButtonText(
                            MoneyMapCategory.INCOME,
                        )}
                        onPressCta={() => {
                            setFormState(FormState.INCOME);
                        }}
                        hideButtonIcon={
                            moneyMap.incomeMoneyMapAccountId != null
                        }
                    />
                    <EditorSectionContainer
                        category={MoneyMapCategory.BILLS}
                        amountCents={billsTotalCents}
                        rules={billRules}
                        moneyMap={moneyMap}
                        ctaButtonText={getCtaButtonText(MoneyMapCategory.BILLS)}
                        onPressCta={() => {
                            setFormState(FormState.BILLS);
                        }}
                        onPressRule={(identifier: string) => {
                            setSelectedRuleIdentifier(identifier);
                            setFormState(FormState.BILLS);
                        }}
                        disabled={disableNonIncomeSections}
                    />
                    <EditorSectionContainer
                        category={MoneyMapCategory.SPEND}
                        amountCents={spendTotalCents}
                        rules={spendRules}
                        moneyMap={moneyMap}
                        ctaButtonText={getCtaButtonText(MoneyMapCategory.SPEND)}
                        onPressCta={() => {
                            setFormState(FormState.SPEND);
                        }}
                        onPressRule={(identifier: string) => {
                            setSelectedRuleIdentifier(identifier);
                            setFormState(FormState.SPEND);
                        }}
                        disabled={disableNonIncomeSections}
                    />
                    <EditorSectionContainer
                        category={MoneyMapCategory.GOALS}
                        amountCents={goalsTotalCents}
                        rules={goalRules}
                        moneyMap={moneyMap}
                        ctaButtonText={getCtaButtonText(MoneyMapCategory.GOALS)}
                        onPressCta={() => {
                            setFormState(FormState.GOAL_PRESETS);
                        }}
                        onPressRule={(identifier: string) => {
                            setSelectedRuleIdentifier(identifier);
                            setFormState(FormState.GOALS);
                        }}
                        onPressGroup={(groupId: string) => {
                            setSelectedGroupId(groupId);
                            setFormState(FormState.GROUP);
                        }}
                        disabled={disableNonIncomeSections}
                        onPressAddGroup={() => {
                            setFormState(FormState.GROUP);
                        }}
                        onPressAddGoalToGroup={(groupId: string) => {
                            setSelectedGroupId(groupId);
                            setFormState(FormState.GOAL_PRESETS);
                        }}
                        groups={moneyMap.moneyMapRuleGroups}
                    />
                    <EditorSectionContainer
                        category={MoneyMapCategory.OTHER_ACCOUNTS}
                        amountCents={null}
                        rules={otherAccountRules}
                        moneyMap={moneyMap}
                        ctaButtonText={getCtaButtonText(
                            MoneyMapCategory.OTHER_ACCOUNTS,
                        )}
                        onPressCta={() => {
                            setFormState(FormState.OTHER_ACCOUNTS);
                        }}
                        onPressRule={(identifier: string) => {
                            setSelectedRuleIdentifier(identifier);
                            setFormState(FormState.OTHER_ACCOUNTS);
                        }}
                        disabled={disableNonIncomeSections}
                    />
                </YStack>
            </YStack>
            {formState === FormState.INCOME && (
                <IncomeForm
                    moneyMap={moneyMap}
                    onPressSave={onSaveForm}
                    onClose={closeForm}
                    presentation={formPresentation}
                />
            )}
            {formState === FormState.BILLS && (
                <BillsSpendForm
                    ruleType={MoneyMapRuleType.BILLS}
                    moneyMap={moneyMap}
                    onPressSave={onSaveForm}
                    onPressRemove={onPressRemoveRule}
                    priority={
                        // Priority is either priority of selected rule, or +1 of how many bills rules exist
                        selectedBillsRule?.priority ??
                        calculateNewRulePriority(
                            billRules,
                            spendRules,
                            goalRules,
                            MoneyMapRuleType.BILLS,
                        )
                    }
                    rule={selectedBillsRule}
                    onClose={closeForm}
                    presentation={formPresentation}
                />
            )}
            {formState === FormState.SPEND && (
                <BillsSpendForm
                    ruleType={MoneyMapRuleType.SPEND}
                    moneyMap={moneyMap}
                    onPressSave={onSaveForm}
                    onPressRemove={onPressRemoveRule}
                    priority={
                        // Priority is either priority of selected rule, or +1 of how many bills + spend rules exist
                        selectedSpendRule?.priority ??
                        calculateNewRulePriority(
                            billRules,
                            spendRules,
                            goalRules,
                            MoneyMapRuleType.SPEND,
                        )
                    }
                    rule={selectedSpendRule}
                    onClose={closeForm}
                    presentation={formPresentation}
                />
            )}
            {formState === FormState.GOALS && (
                <GoalForm
                    moneyMap={moneyMap}
                    onPressSave={onSaveForm}
                    onPressRemove={onPressRemoveRule}
                    priority={
                        // Priority is either priority of selected rule, or +1 of how many bills + spend + goal rules exist
                        // Goals stay ahead of Other accounts in the top-level section order.
                        selectedGoalRule?.priority ??
                        selectedGroup?.priority ??
                        calculateNewRulePriority(
                            billRules,
                            spendRules,
                            goalRules,
                            MoneyMapRuleType.GOAL,
                            moneyMap.moneyMapRuleGroups,
                        )
                    }
                    rule={selectedGoalRule}
                    group={selectedGroup}
                    rulePreset={
                        rulePreset != null
                            ? GoalPresetRuleConfigs[rulePreset]
                            : undefined
                    }
                    presetHelpText={
                        rulePreset != null
                            ? GoalPresetHelpText[rulePreset]
                            : undefined
                    }
                    backdoorRothUserIndex={
                        rulePreset === GoalPreset.BACKDOOR_ROTH_PARTNER ? 1 : 0
                    }
                    onClose={closeForm}
                    presentation={formPresentation}
                />
            )}
            {formState === FormState.OTHER_ACCOUNTS && (
                <OtherAccountsForm
                    rule={selectedOtherAccountRule}
                    priority={
                        // Other accounts are always appended after the existing Bills, Spend, and Goals sections.
                        selectedOtherAccountRule?.priority ??
                        calculateNewRulePriority(
                            billRules,
                            spendRules,
                            goalRules,
                            MoneyMapRuleType.ACCOUNT_ONLY,
                            moneyMap.moneyMapRuleGroups,
                            otherAccountRules,
                        )
                    }
                    moneyMap={moneyMap}
                    onPressSave={onSaveForm}
                    onPressRemove={onPressRemoveRule}
                    onClose={closeForm}
                    presentation={formPresentation}
                />
            )}
            {formState === FormState.GOAL_PRESETS && (
                <GoalPresetSelection
                    onPressCancel={() => {
                        setFormState(FormState.CLOSED);
                    }}
                    onPressNext={(preset: GoalPreset) => {
                        setRulePreset(preset);
                        setFormState(FormState.GOALS);
                    }}
                    onClose={() => {
                        setFormState(FormState.CLOSED);
                    }}
                    presentation={formPresentation}
                />
            )}
            {formState === FormState.GROUP && (
                <GroupForm
                    moneyMap={moneyMap}
                    group={selectedGroup}
                    priority={
                        selectedGroup?.priority ??
                        calculateNewRulePriority(
                            billRules,
                            spendRules,
                            goalRules,
                            MoneyMapRuleType.GOAL,
                            moneyMap.moneyMapRuleGroups,
                        )
                    }
                    onPressSave={onSaveForm}
                    onPressRemove={onPressRemoveGroup}
                    onClose={closeForm}
                    presentation={formPresentation}
                />
            )}
        </XStack>
    );
};

export default MoneyMapEditor;
